import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayerInfo } from '../../models/PlayerInfo';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { CACHE_KEYS, entriesUrl } from '../../utils/apiEndpoints';
import { DEFAULT_TEAM_SLUG, playerProfilePath } from '../../utils/playerProfile';
import {
  ErrorState,
  LoadingState,
  NoPlayersState,
  PageSubtitle,
  PageTitle,
  PlayerHeroCard,
  PlayersGrid,
  RostersContainer,
  SeasonSelect,
  SeasonSelector,
} from './Rosters.Styles';

const playerImagePath = (playerName: string) =>
  `/images/players/${playerName.replace(/\s+/g, '-')}.png`;

type HistoricPlayersBySeason = Record<string, PlayerInfo[]>;
const CURRENT_SEASON_VALUE = 'current';

const Rosters: React.FC = () => {
  const navigate = useNavigate();
  const [currentPlayers, setCurrentPlayers] = useState<PlayerInfo[]>([]);
  const [historicPlayers, setHistoricPlayers] = useState<HistoricPlayersBySeason>({});
  const [selectedSeason, setSelectedSeason] = useState<string>(CURRENT_SEASON_VALUE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let isMounted = true;

    const fetchPlayers = async () => {
      try {
        const CACHE_TTL_MS = 5 * 60 * 1000;
        const currentItemsPromise = fetchItemsWithCache<PlayerInfo>(
          entriesUrl('nomads_players'),
          CACHE_KEYS.players,
          CACHE_TTL_MS
        );

        const historicItemsPromise = fetch('/nomads/historic/players.json')
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Historic roster request failed with status ${response.status}`);
            }
            return response.json();
          })
          .catch((historicError) => {
            console.warn('Historic roster file unavailable:', historicError);
            return {} as HistoricPlayersBySeason;
          });

        const [currentItems, historicItems] = await Promise.all([
          currentItemsPromise,
          historicItemsPromise,
        ]);

        if (!isMounted) return;
        setCurrentPlayers(currentItems ?? []);
        setHistoricPlayers((historicItems ?? {}) as HistoricPlayersBySeason);
        setLoading(false);
      } catch (err) {
        console.error('Error loading roster:', err);
        if (!isMounted) return;
        setError('Unable to load roster. Please try again later.');
        setLoading(false);
      }
    };

    fetchPlayers();
    return () => {
      isMounted = false;
    };
  }, []);

  const onImageError = useCallback((playerName: string) => {
    setImageErrors((prev) => {
      if (prev[playerName]) return prev;
      return { ...prev, [playerName]: true };
    });
  }, []);

  const seasonOptions = useMemo(() => {
    const historicSeasons = Object.keys(historicPlayers).sort((a, b) => b.localeCompare(a));
    return [CURRENT_SEASON_VALUE, ...historicSeasons];
  }, [historicPlayers]);

  const selectedPlayers = useMemo(() => {
    if (selectedSeason === CURRENT_SEASON_VALUE) {
      return currentPlayers;
    }
    return historicPlayers[selectedSeason] ?? [];
  }, [currentPlayers, historicPlayers, selectedSeason]);

  const sortedPlayers = useMemo(() => {
    return [...selectedPlayers].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedPlayers]);

  if (loading) {
    return (
      <RostersContainer>
        <LoadingState>Loading roster...</LoadingState>
      </RostersContainer>
    );
  }

  if (error) {
    return (
      <RostersContainer>
        <ErrorState>{error}</ErrorState>
      </RostersContainer>
    );
  }

  return (
    <RostersContainer>
      <PageTitle>Team Roster</PageTitle>
      <PageSubtitle>Meet the squad</PageSubtitle>
      <SeasonSelector>
        <label htmlFor="season-select">Season</label>
        <SeasonSelect
          id="season-select"
          value={selectedSeason}
          onChange={(event) => setSelectedSeason(event.target.value)}
        >
          {seasonOptions.map((season) => (
            <option key={season} value={season}>
              {season === CURRENT_SEASON_VALUE ? 'Current Season' : season}
            </option>
          ))}
        </SeasonSelect>
      </SeasonSelector>
      {sortedPlayers.length === 0 ? (
        <NoPlayersState>No players found for the selected season.</NoPlayersState>
      ) : null}
      <PlayersGrid>
        {sortedPlayers.map((player) => (
          <PlayerHeroCard
            key={player.name}
            role="link"
            tabIndex={0}
            onClick={() => navigate(playerProfilePath(DEFAULT_TEAM_SLUG, player.name))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(playerProfilePath(DEFAULT_TEAM_SLUG, player.name));
              }
            }}
          >
            <img
              src={
                imageErrors[player.name]
                  ? '/images/players/placeholder-player.jpg'
                  : playerImagePath(player.name)
              }
              alt={player.name}
              onError={() => onImageError(player.name)}
            />
            <div className="card-content">
              <h3 className="player-name">{player.name}</h3>
              <p className="player-meta">Position: {player.position}</p>
              <p className="player-meta">Jersey: #{player.number ?? 0}</p>
              <p className="player-meta">Main Team: {player.mainTeam || 'N/A'}</p>
            </div>
          </PlayerHeroCard>
        ))}
      </PlayersGrid>
    </RostersContainer>
  );
};

export default Rosters;
